import React, { useState, useEffect, useCallback, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import TableRowSkeleton from "../TableRowSkeleton";
import { i18n } from "../../translate/i18n";

// Painel de uso de todos os clientes (empresas), aba "Uso do Sistema" da tela
// de Gerenciamento. Objetivo: descobrir de relance quem parou de usar o sistema.
//
// A lista inteira vem numa tacada só (são dezenas de empresas); busca, filtro e
// ordenação são feitos no cliente. Só o período vai para o backend, porque muda
// as colunas de volume.

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  filters: {
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    width: "100%",
    overflowX: "auto",
    ...theme.scrollbarStyles,
  },
  table: {
    // Larguras fixas por coluna: sem isso o navegador dá espaço demais para as
    // legendas e as últimas colunas saem da tela.
    tableLayout: "fixed",
    minWidth: 1240,
  },
  headerCell: {
    whiteSpace: "nowrap",
    padding: theme.spacing(1),
  },
  cell: {
    padding: theme.spacing(1),
    verticalAlign: "top",
    // Segura as legendas nowrap dentro da própria coluna: sem isto elas
    // invadem visualmente a coluna vizinha.
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  nowrap: {
    whiteSpace: "nowrap",
  },
  caption: {
    display: "block",
    lineHeight: 1.3,
  },
  chip: {
    color: "#fff",
    fontWeight: 500,
  },
  online: {
    color: theme.palette.type === "dark" ? "#a5d6a7" : "#1b5e20",
    fontWeight: 500,
  },
  offline: {
    color: theme.palette.type === "dark" ? "#ef9a9a" : "#b71c1c",
    fontWeight: 500,
  },
  inactiveRow: {
    opacity: 0.6,
  },
}));

// Cores sólidas com texto branco funcionam nos dois temas sem depender de
// palette.type, ao contrário dos chips claros usados no Relatório de Mensagens.
// labelKey é traduzido na renderização para acompanhar a troca de idioma.
const HEALTH = {
  ativo: { labelKey: "clientUsageManager.health.ativo", color: "#4caf50" },
  atencao: { labelKey: "clientUsageManager.health.atencao", color: "#ff9800" },
  inativo: { labelKey: "clientUsageManager.health.inativo", color: "#f44336" },
};

const PERIOD_PRESETS = [
  { value: "7", labelKey: "clientUsageManager.periods.last7" },
  { value: "30", labelKey: "clientUsageManager.periods.last30" },
  { value: "90", labelKey: "clientUsageManager.periods.last90" },
  { value: "custom", labelKey: "clientUsageManager.periods.custom" },
];

const SITUATION_FILTERS = [
  { value: "todos", labelKey: "clientUsageManager.situations.todos" },
  { value: "ativo", labelKey: "clientUsageManager.situations.ativo" },
  { value: "atencao", labelKey: "clientUsageManager.situations.atencao" },
  { value: "inativo", labelKey: "clientUsageManager.situations.inativo" },
];

const toISODate = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const daysAgoISO = (days) =>
  toISODate(new Date(Date.now() - days * 24 * 60 * 60 * 1000));

// O backend devolve DATETIME como string ("YYYY-MM-DD HH:mm:ss") por causa do
// dateStrings da conexão MySQL; o Safari não parseia esse formato, então a troca
// do espaço por "T" mantém o comportamento igual em todos os browsers.
const parseDate = (value) => {
  if (!value) return null;
  const parsed = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTime = (value) => {
  const parsed = parseDate(value);
  if (!parsed) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

const formatDate = (value) => {
  const parsed = parseDate(value);
  if (!parsed) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(parsed);
};

const formatNumber = (value) => Number(value || 0).toLocaleString("pt-BR");

const formatIdle = (daysIdle) => {
  if (daysIdle === null || daysIdle === undefined)
    return i18n.t("clientUsageManager.idle.never");
  if (daysIdle <= 0) return i18n.t("clientUsageManager.idle.today");
  return i18n.t("common.time.daysAgo", { count: daysIdle });
};

// Vencimento "9999-12-31" é a convenção do projeto para "não vence".
const formatDueDate = (dueDate) => {
  if (!dueDate) return i18n.t("clientUsageManager.dueDate.none");
  const year = Number(String(dueDate).substring(0, 4));
  if (!year || year > 2037) return i18n.t("clientUsageManager.dueDate.none");
  return i18n.t("clientUsageManager.dueDate.due", { date: formatDate(dueDate) });
};

// Empresa sem atividade nenhuma ordena como a mais parada de todas.
const idleForSort = (row) =>
  row.daysIdle === null || row.daysIdle === undefined
    ? Number.MAX_SAFE_INTEGER
    : row.daysIdle;

const SORTERS = {
  name: (row) => String(row.name || "").toLowerCase(),
  daysIdle: idleForSort,
  lastMessageSentAt: (row) => parseDate(row.lastMessageSentAt)?.getTime() ?? -1,
  lastLoginAt: (row) => parseDate(row.lastLoginAt)?.getTime() ?? -1,
  connectionsOnline: (row) => row.connectionsOnline,
  usersTotal: (row) => row.usersTotal,
  volume: (row) => row.messagesSentInPeriod + row.messagesReceivedInPeriod,
  contactsTotal: (row) => row.contactsTotal,
};

export default function ClientUsageManager() {
  const classes = useStyles();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [preset, setPreset] = useState("30");
  const [initialDate, setInitialDate] = useState(daysAgoISO(29));
  const [finalDate, setFinalDate] = useState(toISODate(new Date()));

  const [search, setSearch] = useState("");
  const [situation, setSituation] = useState("todos");
  const [showDisabled, setShowDisabled] = useState(false);

  const [orderBy, setOrderBy] = useState("daysIdle");
  const [order, setOrder] = useState("desc");

  const handlePresetChange = (value) => {
    setPreset(value);
    if (value === "custom") return;
    setInitialDate(daysAgoISO(Number(value) - 1));
    setFinalDate(toISODate(new Date()));
  };

  const loadUsage = useCallback(
    async (refresh = false) => {
      setLoading(true);
      try {
        const { data } = await api.get("/companies/usage", {
          params: {
            initialDate,
            finalDate,
            ...(refresh ? { refresh: 1 } : {}),
          },
        });
        setRows(Array.isArray(data?.companies) ? data.companies : []);
      } catch (err) {
        toastError(err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [initialDate, finalDate]
  );

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const disabledCount = useMemo(
    () => rows.filter((row) => row.status === false).length,
    [rows]
  );

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = rows
      .filter((row) => showDisabled || row.status !== false)
      .filter((row) => !term || String(row.name || "").toLowerCase().includes(term))
      .filter((row) => situation === "todos" || row.activityLevel === situation);

    const sorter = SORTERS[orderBy] || SORTERS.daysIdle;
    const direction = order === "asc" ? 1 : -1;

    return [...filtered].sort((a, b) => {
      const left = sorter(a);
      const right = sorter(b);
      if (left < right) return -1 * direction;
      if (left > right) return 1 * direction;
      return String(a.name).localeCompare(String(b.name));
    });
  }, [rows, search, situation, showDisabled, orderBy, order]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder(order === "asc" ? "desc" : "asc");
      return;
    }
    setOrderBy(field);
    // Nomes começam em A-Z; métricas começam pelo maior (o mais parado no topo).
    setOrder(field === "name" ? "asc" : "desc");
  };

  const sortableHeader = (field, label, align = "left") => (
    <TableCell align={align} className={classes.headerCell}>
      <TableSortLabel
        active={orderBy === field}
        direction={orderBy === field ? order : "desc"}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const COLUMN_WIDTHS = [210, 105, 165, 165, 140, 130, 175, 150];

  const clearFilters = () => {
    setSearch("");
    setSituation("todos");
    setShowDisabled(false);
    handlePresetChange("30");
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={1} alignItems="center" className={classes.filters}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label={i18n.t("clientUsageManager.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <TextField
            select
            fullWidth
            size="small"
            variant="outlined"
            label={i18n.t("clientUsageManager.volumePeriod")}
            value={preset}
            onChange={(e) => handlePresetChange(e.target.value)}
          >
            {PERIOD_PRESETS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {i18n.t(option.labelKey)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {preset === "custom" && (
          <>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                variant="outlined"
                label={i18n.t("common.from")}
                value={initialDate}
                onChange={(e) => setInitialDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                variant="outlined"
                label={i18n.t("common.to")}
                value={finalDate}
                onChange={(e) => setFinalDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12} sm={6} md={2}>
          <TextField
            select
            fullWidth
            size="small"
            variant="outlined"
            label={i18n.t("clientUsageManager.situation")}
            value={situation}
            onChange={(e) => setSituation(e.target.value)}
          >
            {SITUATION_FILTERS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {i18n.t(option.labelKey)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md="auto">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showDisabled}
                onChange={(e) => setShowDisabled(e.target.checked)}
              />
            }
            label={i18n.t("clientUsageManager.showDisabled", { total: disabledCount })}
          />
        </Grid>

        <Grid item xs={12} sm={6} md="auto">
          <Box display="flex" gridGap={8}>
            <Button
              size="small"
              variant="outlined"
              disabled={loading}
              onClick={() => loadUsage(true)}
            >
              {i18n.t("common.refresh")}
            </Button>
            <Button size="small" variant="outlined" onClick={clearFilters}>
              {i18n.t("clientUsageManager.clearFilters")}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
        {i18n.t("clientUsageManager.legend")}
      </Typography>

      <Paper variant="outlined" className={classes.tableContainer}>
        <Table size="small" className={classes.table}>
          <colgroup>
            {COLUMN_WIDTHS.map((width, index) => (
              <col key={index} style={{ width }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {sortableHeader("name", i18n.t("clientUsageManager.columns.client"))}
              {sortableHeader("daysIdle", i18n.t("clientUsageManager.situation"))}
              {sortableHeader(
                "lastMessageSentAt",
                i18n.t("clientUsageManager.columns.lastMessage")
              )}
              {sortableHeader("lastLoginAt", i18n.t("clientUsageManager.columns.lastLogin"))}
              {sortableHeader(
                "connectionsOnline",
                i18n.t("clientUsageManager.columns.connections")
              )}
              {sortableHeader("usersTotal", i18n.t("clientUsageManager.columns.users"))}
              {sortableHeader("volume", i18n.t("clientUsageManager.columns.volume"))}
              <TableCell className={classes.headerCell}>
                {i18n.t("clientUsageManager.columns.plan")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={8} />
            ) : (
              <>
                {visibleRows.map((row) => {
                  const health = HEALTH[row.activityLevel] || HEALTH.inativo;
                  const hasConnections = row.connectionsTotal > 0;

                  return (
                    <TableRow
                      key={row.id}
                      hover
                      className={row.status === false ? classes.inactiveRow : undefined}
                    >
                      <TableCell className={classes.cell}>
                        {row.name}
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          className={classes.caption}
                        >
                          {row.status === false
                            ? i18n.t("clientUsageManager.companyDisabled")
                            : i18n.t("clientUsageManager.since", {
                                date: formatDate(row.createdAt),
                              })}{" "}
                          ·{" "}
                          {i18n.t("clientUsageManager.contacts", {
                            total: formatNumber(row.contactsTotal),
                          })}
                        </Typography>
                      </TableCell>

                      <TableCell className={classes.cell}>
                        <Chip
                          size="small"
                          label={i18n.t(health.labelKey)}
                          className={classes.chip}
                          style={{ backgroundColor: health.color }}
                        />
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          className={classes.caption}
                        >
                          {formatIdle(row.daysIdle)}
                        </Typography>
                      </TableCell>

                      <TableCell className={`${classes.cell} ${classes.nowrap}`}>
                        {i18n.t("clientUsageManager.sentAt", {
                          date: formatDateTime(row.lastMessageSentAt),
                        })}
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          className={classes.caption}
                        >
                          {i18n.t("clientUsageManager.receivedAt", {
                            date: formatDateTime(row.lastMessageReceivedAt),
                          })}
                        </Typography>
                      </TableCell>

                      <TableCell className={`${classes.cell} ${classes.nowrap}`}>
                        {row.lastLoginIsEstimated && row.lastLoginAt ? (
                          <Tooltip title={i18n.t("clientUsageManager.loginEstimated")}>
                            <span>~{formatDateTime(row.lastLoginAt)}</span>
                          </Tooltip>
                        ) : (
                          formatDateTime(row.lastLoginAt)
                        )}
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          className={classes.caption}
                          noWrap
                        >
                          {row.lastLoginUserName || i18n.t("clientUsageManager.noUser")}
                        </Typography>
                      </TableCell>

                      <TableCell className={`${classes.cell} ${classes.nowrap}`}>
                        <span
                          className={
                            row.connectionsOnline > 0 ? classes.online : classes.offline
                          }
                        >
                          {row.connectionsOnline}/{row.connectionsTotal} online
                        </span>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          className={classes.caption}
                        >
                          {hasConnections
                            ? i18n.t("clientUsageManager.connectionsCaption", {
                                limit: row.connectionsLimit,
                                drops: formatNumber(row.disconnectsInPeriod),
                              })
                            : i18n.t("clientUsageManager.noConnection")}
                        </Typography>
                      </TableCell>

                      <TableCell className={`${classes.cell} ${classes.nowrap}`}>
                        {row.usersTotal}/{row.usersLimit}
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          className={classes.caption}
                        >
                          {i18n.t("clientUsageManager.usersCaption", {
                            active: row.usersActiveIn30Days,
                            online: row.usersOnline,
                          })}
                        </Typography>
                      </TableCell>

                      <TableCell className={`${classes.cell} ${classes.nowrap}`}>
                        {i18n.t("clientUsageManager.volumeLine", {
                          sent: formatNumber(row.messagesSentInPeriod),
                          received: formatNumber(row.messagesReceivedInPeriod),
                        })}
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          className={classes.caption}
                        >
                          {i18n.t("clientUsageManager.ticketsCaption", {
                            created: formatNumber(row.ticketsCreatedInPeriod),
                            finished: formatNumber(row.ticketsFinishedInPeriod),
                            active: formatNumber(row.ticketsActive),
                          })}
                        </Typography>
                      </TableCell>

                      <TableCell className={classes.cell}>
                        {row.plan?.name || i18n.t("clientUsageManager.noPlan")}
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          className={classes.caption}
                        >
                          {formatDueDate(row.dueDate)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {visibleRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {i18n.t("clientUsageManager.empty")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </Paper>
    </div>
  );
}
